import { Button } from '@/components/ui/button';
import React, { useEffect, useState } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation as useWouterLocation } from 'wouter';
import { getCurrentUser } from '@/lib/auth';
import { authInterceptor } from '@/lib/auth-interceptor';

export default function Help() {
  const { currentOrganizationId, currentOrganization } = useOrganization();
  // We will not switch org anymore; show embedded read-only headlines
  const [redirecting, setRedirecting] = useState(true);
  const [, setLocation] = useWouterLocation();
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [locationName, setLocationName] = useState<string>('');
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketReadState, setTicketReadState] = useState<'loading'|'ready'|'error'>('loading');
  const [threadReadState, setThreadReadState] = useState<'loading'|'ready'|'error'>('loading');
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [thread, setThread] = useState<{ticket:any, comments:any[]} | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [addInternal, setAddInternal] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const currentUser = getCurrentUser();
  const [isAgent, setIsAgent] = useState<boolean>(false);
  // Separate state for ticket form vs quick post to avoid unintended coupling
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [postBody, setPostBody] = useState('');
  const [ticketPriority, setTicketPriority] = useState<'P1'|'P2'|'P3'>('P3');

  useEffect(() => {
    const run = async () => {
      try {
        // Determine agent accurately from server
        const me = await fetch('/api/auth/me', { credentials: 'include', redirect: 'error' });
        if (me.ok) {
          const { user } = await me.json();
          setIsAgent(!!(user?.isSupportAgent || user?.isSuperAdmin));
        }
        const url = currentOrganizationId ? `/api/help-support/resolve?organizationId=${currentOrganizationId}` : '/api/help-support/resolve';
        const res = await fetch(url, { credentials: 'include', redirect: 'error' });
        const data = await res.json();
        if (res.ok && data.status === 'ok' && data.locationId) {
          // Load read-only headlines without org switch
          const res2 = await fetch(`/api/help-support/headlines${currentOrganizationId ? `?organizationId=${currentOrganizationId}` : ''}`, { credentials: 'include', redirect: 'error' });
          const data2 = await res2.json();
          if (res2.ok) {
            setItems(data2.items || []);
            setLocationName(data2.locationName || 'BoltOS Support Center');
          } else {
            setError('Could not load support headlines.');
          }
        }
        if (data.status === 'missing') {
          setError(`Support location not found. Ask admin to create: "${data.suggestedName}"`);
        } else {
          setError('Could not resolve support location.');
        }
      } catch (e: any) {
        setError(e?.message || 'Unexpected error');
      } finally {
        setRedirecting(false);
      }
    };
    run();
  }, [currentOrganizationId]);

  useEffect(() => {
    let cancelled = false;
    setTickets([]);
    setActiveTicketId(null);
    setThread(null);
    setTicketReadState('loading');
    const loadTickets = async () => {
      try {
        const url = currentOrganizationId ? `/api/support/tickets?organizationId=${currentOrganizationId}` : '/api/support/tickets';
        const r = await fetch(url, { credentials: 'include', redirect: 'error' });
        if (!r.ok) throw new Error('Ticket read failed');
        const data = await r.json();
        if (!Array.isArray(data)) throw new Error('Invalid ticket list');
        if (!cancelled) {
          setTickets(data);
          setTicketReadState('ready');
        }
      } catch {
        if (!cancelled) setTicketReadState('error');
      }
    };
    if (!redirecting) loadTickets();
    return () => { cancelled = true; };
  }, [redirecting, currentOrganizationId]);

  useEffect(() => {
    let cancelled = false;
    setThread(null);
    setThreadReadState('loading');
    const loadThread = async () => {
      if (!activeTicketId) return;
      try {
        const r = await fetch(`/api/support/tickets/${activeTicketId}`, { credentials: 'include', redirect: 'error' });
        if (!r.ok) throw new Error('Thread read failed');
        const data = await r.json();
        if (data?.ticket?.id !== activeTicketId || !Array.isArray(data.comments)) throw new Error('Invalid ticket thread');
        if (!cancelled) {
          setThread(data);
          setThreadReadState('ready');
        }
      } catch {
        if (!cancelled) setThreadReadState('error');
      }
    };
    loadThread();
    return () => { cancelled = true; };
  }, [activeTicketId, currentOrganizationId]);

  if (redirecting) return <div className="p-8">Loading support…</div>;
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Help &amp; Support</h1>
      <div className="text-sm text-muted-foreground">{`BoltOS Support Center - ${currentOrganization?.name || (locationName || '')}`}</div>
      {!items.length && error && <div className="text-red-500">{error}</div>}
      <div className="space-y-4">
        <div className="rounded border border-neutral-700 p-4">
          <div className="font-semibold mb-2">Create Support Ticket</div>
          <div className="space-y-2">
            <input className="w-full bg-transparent border border-neutral-700 rounded px-2 py-1" placeholder="Subject" value={ticketSubject} onChange={(e)=>setTicketSubject(e.target.value)} />
            <textarea className="w-full bg-transparent border border-neutral-700 rounded px-2 py-2" rows={4} placeholder="Describe your issue" value={ticketDescription} onChange={(e)=>setTicketDescription(e.target.value)} />
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-neutral-400">Priority:</span>
              {(['P1','P2','P3'] as const).map(p => (
                <Button key={p} variant={ticketPriority === p ? "default" : "outline"} aria-pressed={ticketPriority === p} className="min-h-11" onClick={()=>setTicketPriority(p)}>{p}: {p === 'P1' ? 'Urgent' : p === 'P2' ? 'High' : 'Normal'}</Button>
              ))}
            </div>
            <Button className="min-h-11" onClick={async()=>{
              if (!ticketSubject || !ticketDescription) return;
              const createUrl = currentOrganizationId ? `/api/support/tickets?organizationId=${currentOrganizationId}` : '/api/support/tickets';
              const r = await authInterceptor.fetch(createUrl, {
                method:'POST', 
                headers:{'Content-Type':'application/json'}, 
                credentials:'include', 
                body: JSON.stringify({ subject: ticketSubject, description: ticketDescription, priority: ticketPriority })
              });
              if (r.ok) {
                setTicketSubject('');
                setTicketDescription('');
                setTicketPriority('P3');
                const listUrl = currentOrganizationId ? `/api/support/tickets?organizationId=${currentOrganizationId}` : '/api/support/tickets';
                const list = await fetch(listUrl,{credentials:'include', redirect: 'error'});
                if(list.ok) setTickets(await list.json());
              } else {
                const error = await r.json();
                console.error('Failed to create ticket:', error);
              }
            }}>Submit</Button>
          </div>
        </div>

        <div className="rounded border border-neutral-700 p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-semibold">My Tickets</div>
            <Button
              className="text-xs border border-neutral-700 rounded px-2 py-1 hover:bg-neutral-800"
              onClick={()=>setShowResolved(v=>!v)}
            >
              {showResolved ? 'Hide Resolved/Closed' : 'Show Resolved/Closed'}
            </Button>
          </div>
          {tickets
            .filter(t => showResolved ? true : (t.status !== 'resolved' && t.status !== 'closed'))
            .map(t => (
            <Button key={t.id} variant="ghost" className={`h-auto min-h-11 block whitespace-normal w-full text-left border-t border-neutral-200 py-2 first:border-t-0 ${activeTicketId===t.id?'bg-slate-100':''}`} onClick={()=>setActiveTicketId(t.id)}>
              <div className="font-medium flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-600/20 text-blue-400 capitalize">{t.status || 'new'}</span>
                {t.subject}
              </div>
              <div className="text-xs text-neutral-400">{t.priority || 'P3'} • {new Date(t.created_at).toLocaleString()} • By {t.created_by_name || 'Unknown'}</div>
            </Button>
          ))}
          {ticketReadState === 'loading' && <div role="status">Loading tickets…</div>}
          {ticketReadState === 'error' && <div role="alert">Could not load tickets. Please reload to try again.</div>}
          {ticketReadState === 'ready' && tickets.length===0 && <div className="text-sm text-neutral-400">No tickets yet.</div>}
        </div>

        {activeTicketId && threadReadState === 'loading' && <div role="status">Loading ticket details…</div>}
        {activeTicketId && threadReadState === 'error' && <div role="alert">Could not load ticket details. Please reload to try again.</div>}
        {activeTicketId && thread && (
          <div className="rounded border border-neutral-700 p-4">
            <div className="font-semibold mb-2">Ticket Details</div>
            <div className="mb-3">
              <div className="text-base font-semibold">{thread.ticket.subject}</div>
              <div className="text-xs text-neutral-400">{thread.ticket.status} • {thread.ticket.priority} • {new Date(thread.ticket.created_at).toLocaleString()} • By {tickets.find(x=>x.id===thread.ticket.id)?.created_by_name || 'Unknown'}</div>
              <div className="mt-2 whitespace-pre-line text-sm text-neutral-200">{thread.ticket.description}</div>
            </div>
            {/* Agent status controls */}
            {isAgent && (
            <div className="mb-3 flex flex-wrap gap-2 items-center">
              <span className="text-xs text-neutral-400">Set status:</span>
              {['new','in_progress','waiting_on_customer','resolved','closed'].map(s => (
                <Button key={s} className={`min-h-11 px-3 py-2 rounded text-sm border ${thread.ticket.status===s?'bg-primary text-white':'border-neutral-700'}`} onClick={async()=>{
                  const r = await authInterceptor.fetch(`/api/support/tickets`, { method:'PATCH', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ id: activeTicketId, status: s })});
                  if (r.ok) {
                    const ref = await fetch(`/api/support/tickets/${activeTicketId}`, { credentials:'include', redirect: 'error' });
                    if (ref.ok) setThread(await ref.json());
                    const listUrl = currentOrganizationId ? `/api/support/tickets?organizationId=${currentOrganizationId}` : '/api/support/tickets';
                    const list = await fetch(listUrl,{credentials:'include', redirect: 'error'}); if(list.ok) setTickets(await list.json());
                  }
                }}>{s.replace(/_/g,' ')}</Button>
              ))}
              {(thread.ticket.status==='resolved' || thread.ticket.status==='closed') && (
                <Button className="min-h-11 px-3 py-2 rounded text-sm border border-neutral-700" onClick={async()=>{
                  const r = await authInterceptor.fetch(`/api/support/tickets`, { method:'PATCH', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ id: activeTicketId, status: 'in_progress' })});
                  if (r.ok) {
                    const ref = await fetch(`/api/support/tickets/${activeTicketId}`, { credentials:'include', redirect: 'error' });
                    if (ref.ok) setThread(await ref.json());
                    const listUrl = currentOrganizationId ? `/api/support/tickets?organizationId=${currentOrganizationId}` : '/api/support/tickets';
                    const list = await fetch(listUrl,{credentials:'include', redirect: 'error'}); if(list.ok) setTickets(await list.json());
                  }
                }}>reopen</Button>
              )}
              <span className="ml-4 text-xs text-neutral-400">Priority:</span>
              {(['P1','P2','P3'] as const).map(p => (
                <Button key={p} className={`min-h-11 px-3 py-2 rounded text-sm border ${thread.ticket.priority===p?'bg-primary text-white':'border-neutral-700'}`} onClick={async()=>{
                  const r = await authInterceptor.fetch(`/api/support/tickets`, { method:'PATCH', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ id: activeTicketId, priority: p })});
                  if (r.ok) {
                    const ref = await fetch(`/api/support/tickets/${activeTicketId}`, { credentials:'include', redirect: 'error' });
                    if (ref.ok) setThread(await ref.json());
                    const listUrl = currentOrganizationId ? `/api/support/tickets?organizationId=${currentOrganizationId}` : '/api/support/tickets';
                    const list = await fetch(listUrl,{credentials:'include', redirect: 'error'}); if(list.ok) setTickets(await list.json());
                  }
                }}>{p}: {p === 'P1' ? 'Urgent' : p === 'P2' ? 'High' : 'Normal'}</Button>
              ))}
            </div>
            )}
            <div className="space-y-3">
              {(thread.comments||[]).map(c => (
                <div key={c.id} className="border-t border-neutral-800 pt-2">
                  <div className="text-xs text-neutral-400 mb-1">By {c.author_name || 'Unknown'} • {new Date(c.created_at).toLocaleString()} {c.is_internal ? '• internal' : ''}</div>
                  <div className="text-sm whitespace-pre-line">{c.body}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              <textarea className="w-full bg-transparent border border-neutral-700 rounded px-2 py-2" rows={3} placeholder="Write a reply" value={replyBody} onChange={(e)=>setReplyBody(e.target.value)} />
              <div className="flex items-center gap-3">
                <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={addInternal} onChange={(e)=>setAddInternal(e.target.checked)} /> Internal note (agents)</label>
                <Button className="min-h-11" onClick={async()=>{
                  if (!replyBody) return;
                  const r = await authInterceptor.fetch(`/api/support/tickets/${activeTicketId}/comments`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ body: replyBody, internal: addInternal })});
                  if (r.ok) {
                    setReplyBody('');
                    const ref = await fetch(`/api/support/tickets/${activeTicketId}`, { credentials:'include', redirect: 'error' });
                    if (ref.ok) setThread(await ref.json());
                  }
                }}>Send</Button>
              </div>
            </div>
          </div>
        )}

        <div className="rounded border border-neutral-700 p-4">
          <div className="font-semibold mb-2">Quick Support Post</div>
          <div className="space-y-2">
            <textarea className="w-full bg-transparent border border-neutral-700 rounded px-2 py-2" rows={3} placeholder="Post a quick question to BoltOS Support" value={postBody} onChange={(e)=>setPostBody(e.target.value)} />
            <Button className="min-h-11" onClick={async()=>{
              if (!postBody) return;
              const r = await authInterceptor.fetch(`/api/help-support/posts${currentOrganizationId ? `?organizationId=${currentOrganizationId}`:''}`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body: JSON.stringify({ body: postBody })});
              if (r.ok) {
                setPostBody('');
                const res2 = await fetch(`/api/help-support/headlines${currentOrganizationId ? `?organizationId=${currentOrganizationId}`:''}`, { credentials:'include', redirect: 'error' });
                if (res2.ok) { const d=await res2.json(); setItems(d.items||[]);} }
            }}>Post</Button>
          </div>
        </div>

        {items.map((h) => (
          <div key={h.id} className="rounded border border-neutral-700 p-4">
            <div className="text-base font-semibold">{h.title}</div>
            <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{h.content}</div>
            <div className="text-xs text-neutral-400 mt-2">By {h.author} • Priority {h.priority}</div>
          </div>
        ))}
        {items.length === 0 && !error && <div className="text-sm text-neutral-400">No support headlines yet.</div>}
      </div>
    </div>
  );
}

